import CenteredSpinner from "@/components/CenteredSpinner";
import { Button } from "@/components/ui/button";
import { DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import useOrganizationAiModel from "@/hooks/useOrganizationAiModel";
import { useToast } from "@/hooks/utils/useToast";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const ModelManagementSettings = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const {
        availableModels,
        currentModelId,
        isLoading,
        isError,
        isUpdating,
        assignOrganizationAiModel,
    } = useOrganizationAiModel();

    // Sélection locale : initialisée sur le modèle actif, resynchronisée quand
    // le modèle courant change (chargement initial ou après une mutation réussie).
    const [selectedModelId, setSelectedModelId] = useState<string | undefined>(
        currentModelId,
    );

    useEffect(() => {
        setSelectedModelId(currentModelId);
    }, [currentModelId]);

    const hasPendingChange =
        selectedModelId !== undefined && selectedModelId !== currentModelId;

    const handleCancel = () => {
        setSelectedModelId(currentModelId);
    };

    const handleApply = async () => {
        if (!selectedModelId) {
            return;
        }

        try {
            await assignOrganizationAiModel(selectedModelId);
            toast({
                title: t("settings.modelManagement.toast.success"),
                variant: "success",
            });
        } catch {
            toast({
                title: t("settings.modelManagement.toast.error"),
                variant: "error",
            });
        }
    };

    if (isLoading) {
        return <CenteredSpinner />;
    }

    if (isError) {
        return (
            <p className="p-4 text-sm text-error-base">
                {t("settings.modelManagement.error.load")}
            </p>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-col">
            <DialogHeader className="shrink-0">
                <DialogTitle className="font-bold leading-none">
                    {t("settings.modelManagement.title")}
                </DialogTitle>
                <p className="text-xs">
                    {t("settings.modelManagement.description")}
                </p>
            </DialogHeader>

            <div className="min-h-0 flex-1 overflow-y-auto py-5">
                <RadioGroup
                    value={selectedModelId}
                    onValueChange={setSelectedModelId}
                    className="gap-3"
                >
                    {availableModels.map((model) => {
                        const isSelected = selectedModelId === model.id;
                        const isCurrent = currentModelId === model.id;

                        return (
                            <Label
                                key={model.id}
                                htmlFor={`model-${model.id}`}
                                className={cn(
                                    "flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors",
                                    isSelected
                                        ? "border-surface-active bg-surface-secondary"
                                        : "border-border-primary hover:border-surface-active",
                                )}
                            >
                                <RadioGroupItem
                                    id={`model-${model.id}`}
                                    value={model.id}
                                    className="mt-0.5"
                                />
                                <div className="flex flex-col gap-1">
                                    <span className="flex items-center gap-2 text-sm font-medium">
                                        {model.publicName}
                                        {isCurrent && (
                                            <span className="rounded-full bg-surface-active px-2 py-0.5 text-xs font-normal text-white">
                                                {t(
                                                    "settings.modelManagement.currentBadge",
                                                )}
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-xs text-text-secondary">
                                        {model.technicalName}
                                    </span>
                                </div>
                            </Label>
                        );
                    })}
                </RadioGroup>
            </div>

            {hasPendingChange && (
                <footer className="flex shrink-0 items-center justify-end gap-3 border-t border-border bg-background pt-4 pb-2">
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={isUpdating}
                    >
                        {t("settings.modelManagement.actions.cancel")}
                    </Button>
                    <Button onClick={handleApply} disabled={isUpdating}>
                        {t("settings.modelManagement.actions.apply")}
                    </Button>
                </footer>
            )}
        </div>
    );
};

export default ModelManagementSettings;
